






























































































































































































import { Component, Vue } from "vue-property-decorator";
import { firestore, storage, functions } from "../../firebaseInit";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { ref, getDownloadURL } from "firebase/storage";
import { loadStripe, StripeElements } from "@stripe/stripe-js";
import { PrintSize, isSold, sizesFor, displayImageFor } from "../../models/Art";

@Component({ components: {} })
export default class ArtDetails extends Vue {
  private art: any = null;
  private url: string = "";
  /* Whether the painting is in and can be shown. */
  private hung: boolean = false;
  private notFound: boolean = false;
  private state: "view" | "paying" | "shipping" = "view";
  private paymentIntentLoading: boolean = false;
  private stripe!: any;
  private paymentIntent!: any;
  private stripeElements!: StripeElements;
  private paymentErrorMessage: string = "";
  private selectedSizeLabel: string = "";
  private shipmentDetails: {
    name: string;
    email: string;
    street: string;
    city: string;
    state: string;
    zip: string;
  } = { name: "", email: "", street: "", city: "", state: "", zip: "" };
  private createPaymentIntent = httpsCallable(functions, "createPaymentIntent");

  get priceIsNan() {
    return this.art && isNaN(parseInt(this.art.price?.toString() ?? "", 10));
  }

  /** Originals live in `home-images` and are no longer sold through the site. */
  get isOriginal() {
    return !!this.art && !this.art.isReproduction;
  }

  get soldOriginal() {
    return isSold(this.art);
  }

  get sizes(): PrintSize[] {
    return sizesFor(this.art);
  }

  get selectedSize(): PrintSize | undefined {
    return this.sizes.find((size) => size.label === this.selectedSizeLabel);
  }

  /** What the customer is actually charged: the chosen size, or the flat price. */
  get currentPrice(): number | undefined {
    return this.selectedSize ? this.selectedSize.price : this.art?.price;
  }

  get canBuy() {
    if (!this.art || this.isOriginal) return false;
    if (this.art.soldDate || this.priceIsNan) return false;
    return !!this.currentPrice;
  }

  /**
   * Sends the visitor to the contact page rather than their mail client. A
   * mailto: does nothing at all for anyone without a desktop mail handler
   * registered, which is most people on webmail.
   */
  get enquiryRoute() {
    return {
      path: "/contact",
      query: {
        // The id is what lets the contact page show the piece itself; the name
        // is only there so an old link still reads sensibly.
        piece: this.art?.id,
        about: this.art?.name ?? "",
        sold: this.soldOriginal ? "1" : undefined,
      },
    };
  }

  async created() {
    this.stripe = await loadStripe(
      "pk_live_51L1gblGdMI5Rj1u0H4phwU7fOKvTN0Y3BtdW4kLtrMKxfu5XLlbcwj6E6aJhyesNaUMqSQjgxi2SoClJrkatMkcZ00EhfR0dCL"
    );
    const galleryRef = doc(firestore, "home-images", this.$route.params.id);
    const gallerySnap = await getDoc(galleryRef);

    const reproductionsRef = doc(firestore, "reproductions", this.$route.params.id);
    const reproductionSnap = await getDoc(reproductionsRef);

    if (gallerySnap.exists()) {
      this.art = gallerySnap.data();
      this.art.id = gallerySnap.id;
      this.url = await this.imageFor(this.art);
    } 
    else if (reproductionSnap.exists()) {
      this.art = reproductionSnap.data();
      this.art.id = reproductionSnap.id;
      this.url = await this.imageFor(this.art);    
    }
    else {
      this.notFound = true;
    }

    // Preselect the smallest size so the page always shows a real, buyable price.
    if (this.sizes.length) this.selectedSizeLabel = this.sizes[0].label;

    /*
      A painting already in cache can finish before the load handler is bound,
      and would then sit there invisible waiting for an event that has already
      happened.
    */
    await this.$nextTick();
    const image = this.$refs.image as HTMLImageElement | undefined;
    if (image?.complete && image.naturalWidth) this.hung = true;
  }

  /*
    This page shows a painting about 870px wide and the masters run to ten
    megabytes, so it hangs the display copy, whose long edge is 1600px — more
    than the page can use even on a retina screen. Its address is worked out
    from the painting rather than asked for, so there is no round trip either.
    The original is the fallback for anything that has not yet been through
    `scripts/build-display-images.mjs`.
  */
  async imageFor(art: {
    storageLocation: string;
    displayWidth?: number;
    displayHeight?: number;
  }): Promise<string> {
    const display = displayImageFor(art);
    if (display) return display.url;
    return getDownloadURL(ref(storage, art.storageLocation));
  }

  handleClickBuy() {
    this.state = "shipping";
  }

  async submitDetails() {
    if (
      !(
        this.shipmentDetails.name &&
        this.shipmentDetails.email &&
        this.shipmentDetails.street &&
        this.shipmentDetails.city &&
        this.shipmentDetails.state &&
        this.shipmentDetails.zip
      )
    )
      alert(
        "There is an issue with the details you entered, please try again."
      );
    else this.handlePayment();
  }

  async handlePayment() {
    const price = this.currentPrice;
    if (!price) {
      alert("There has been an issue, please try again.");
      return;
    }
    this.state = "paying";
    this.paymentIntentLoading = true;

    // The size has to travel with the charge — it's the only place Jessie sees
    // which one to print when she fulfils the order.
    const sizeSuffix = this.selectedSize ? ` (${this.selectedSize.label})` : "";

    const paymentIntentResponse: any = await this.createPaymentIntent({
      amount: price * 100,
      description: `ART SOLD: ${this.art.name}${sizeSuffix}, ${new Date().toLocaleString()}`,
      name: this.shipmentDetails.name,
      email: this.shipmentDetails.email,
      street: this.shipmentDetails.street,
      city: this.shipmentDetails.city,
      state: this.shipmentDetails.state,
      zip: this.shipmentDetails.zip,
    }).catch(() => {
      this.paymentIntentLoading = false;
      this.state = "view";
      alert(
        "There has been an error with our payment system. Please try again later."
      );
      return;
    });

    this.paymentIntent = paymentIntentResponse?.data;

    if (this.stripe) {
      this.stripeElements = this.stripe?.elements({
        clientSecret: this.paymentIntent.client_secret,
      });
      const paymentElement = this.stripeElements?.create("payment");
      paymentElement?.mount("#stripe-payment");
    }

    setTimeout(() => {
      this.paymentIntentLoading = false;
    }, 500);
  }

  async submitPayment() {
    if (this.stripe && this.stripeElements) {
      // Hand the chosen size to the confirmation page, which is what actually
      // writes the sale record once Stripe redirects back.
      if (this.selectedSize)
        window.localStorage.setItem(`size:${this.art.id}`, this.selectedSize.label);

      const { error } = await this.stripe?.confirmPayment({
        elements: this.stripeElements,
        confirmParams: {
          // return_url: `http://localhost:8080/payment-confirmed/${this.art?.id}`,
          return_url: `https://art-webapp-812f7.web.app/payment-confirmed/${this.art?.id}`,
        },
      });

      // This point will only be reached if there is an immediate error when
      // confirming the payment. Otherwise, your customer will be redirected to
      // your `return_url`. For some payment methods like iDEAL, your customer will
      // be redirected to an intermediate site first to authorize the payment, then
      // redirected to the `return_url`.

      if (error.type === "card_error" || error.type === "validation_error") {
        this.paymentErrorMessage = error.message || "";
      } else {
        this.paymentErrorMessage = "An unexpected error occured.";
      }

      if (error) window.localStorage.removeItem(`size:${this.art.id}`);
    }
  }
}
